export interface Vistoria {
  id: string;
  codigoCta: string;
  especie: string;
  nomePopular: string;
  projeto: string;
  data: string;
  tipo: string;
  tecnico: string;
  estadoFitossanitario: string;
  classeRisco: string;
  recomendacao: string;
  fotos: number;
  status: string;
  dap: number;
  altura: number;
  diametroCopa: number;
  parecer: string;
  sintomas: string[];
  interferencias: string[];
  municipio: string;
  uf: string;
  bairro: string;
  logradouro: string;
  coordenada: string;
  cicloId: string | null;
}

export interface MonitoringCycle {
  id: string;
  nome: string;
  tipo: string;
  projeto: string;
  dataInicio: string;
  dataFim: string;
  status: string;
  arvoresPrevistas: number;
  vistoriasRealizadas: number;
  progresso: number;
  pendencias: number;
}

export interface TemporalComparison {
  codigoCta: string;
  especie: string;
  nomePopular: string;
  vistoriaAnterior: {
    id: string;
    data: string;
    dap: number;
    altura: number;
    estado: string;
    risco: string;
    recomendacao: string;
  };
  vistoriaAtual: {
    id: string;
    data: string;
    dap: number;
    altura: number;
    estado: string;
    risco: string;
    recomendacao: string;
  };
  tendencia: "agravado" | "reduzido" | "estavel";
}

export const tiposVistoria = [
  "Inventário Inicial",
  "Vistoria Técnica",
  "Remedição",
  "Monitoramento Anual",
  "Monitoramento Semestral",
  "Pós-poda",
  "Pós-replantio",
  "Emergencial",
  "Compensação Ambiental",
  "UC / Área Verde",
];

export const statusVistoria = [
  "Rascunho",
  "Concluída",
  "Em Revisão",
  "Validada",
  "Reprovada",
  "Vencida",
  "Cancelada",
];

export const tipoVistoriaConfig: Record<string, { badge: string; icon: string }> = {
  "Inventário Inicial": { badge: "bg-accent-100 text-accent-700", icon: "ri-search-eye-line" },
  "Vistoria Técnica": { badge: "bg-primary-100 text-primary-700", icon: "ri-clipboard-line" },
  "Remedição": { badge: "bg-secondary-100 text-secondary-700", icon: "ri-ruler-line" },
  "Monitoramento Anual": { badge: "bg-primary-50 text-primary-600", icon: "ri-calendar-check-line" },
  "Monitoramento Semestral": { badge: "bg-accent-50 text-accent-600", icon: "ri-calendar-event-line" },
  "Pós-poda": { badge: "bg-warning-100 text-warning-700", icon: "ri-scissors-cut-line" },
  "Pós-replantio": { badge: "bg-caution-100 text-caution-700", icon: "ri-seedling-line" },
  "Emergencial": { badge: "bg-danger-100 text-danger-700", icon: "ri-alert-line" },
  "Compensação Ambiental": { badge: "bg-primary-100 text-primary-700", icon: "ri-recycle-line" },
  "UC / Área Verde": { badge: "bg-accent-100 text-accent-700", icon: "ri-landscape-line" },
};

export const statusVistoriaConfig: Record<string, { badge: string; label: string }> = {
  "Rascunho": { badge: "bg-background-100 text-foreground-500", label: "Rascunho" },
  "Concluída": { badge: "bg-primary-100 text-primary-700", label: "Concluída" },
  "Em Revisão": { badge: "bg-warning-100 text-warning-700", label: "Em Revisão" },
  "Validada": { badge: "bg-accent-100 text-accent-700", label: "Validada" },
  "Reprovada": { badge: "bg-danger-100 text-danger-700", label: "Reprovada" },
  "Vencida": { badge: "bg-danger-50 text-danger-600 border border-danger-300", label: "Vencida" },
  "Cancelada": { badge: "bg-background-100 text-foreground-500", label: "Cancelada" },
};

export const estadoFitoVistoriaConfig: Record<string, { badge: string; label: string }> = {
  bom: { badge: "bg-primary-100 text-primary-700", label: "Bom" },
  regular: { badge: "bg-caution-100 text-caution-700", label: "Regular" },
  ruim: { badge: "bg-warning-100 text-warning-700", label: "Ruim" },
  critico: { badge: "bg-danger-100 text-danger-700", label: "Crítico" },
  morto: { badge: "bg-background-100 text-foreground-500", label: "Morto" },
};

export const riscoVistoriaConfig: Record<string, { badge: string; label: string }> = {
  baixo: { badge: "bg-primary-100 text-primary-700", label: "Baixo" },
  medio: { badge: "bg-caution-100 text-caution-700", label: "Médio" },
  alto: { badge: "bg-warning-100 text-warning-700", label: "Alto" },
  critico: { badge: "bg-danger-100 text-danger-700", label: "Crítico" },
};

export const kpisVistoria = {
  realizadas: 8742,
  pendentesRevisao: 326,
  vencidas: 94,
  riscoCritico: 87,
  vistoriasMes: 1284,
  arvoresRemonitoradas: 2430,
  fotosAnexadas: 18920,
  pareceresPendentes: 112,
};

export const tecnicos = [
  "Carlos Mendes",
  "Ana Oliveira",
  "Rafael Torres",
  "Juliana Costa",
  "Marcos Vinicius",
  "Pedro Alves",
  "Fernanda Lima",
  "Gabriel Santos",
];

export const vistorias: Vistoria[] = [
  {
    id: "VST-2026-1284",
    codigoCta: "CTA-GO-5208707-000001",
    especie: "Handroanthus chrysotrichus",
    nomePopular: "Ipê-amarelo",
    projeto: "Implantação do CTA — Goiânia Centro",
    data: "2026-06-25",
    tipo: "Vistoria Técnica",
    tecnico: "Carlos Mendes",
    estadoFitossanitario: "bom",
    classeRisco: "baixo",
    recomendacao: "Monitorar",
    fotos: 4,
    status: "Validada",
    dap: 45,
    altura: 12.5,
    diametroCopa: 8.2,
    parecer: "Indivíduo adulto saudável. Copa bem formada, sem sinais de podridão ou pragas. Floração intensa em agosto. Sistema radicular estável sem danos à calçada. Recomenda-se monitoramento anual.",
    sintomas: [],
    interferencias: ["Calçada com leve elevação (2cm)"],
    municipio: "Goiânia",
    uf: "GO",
    bairro: "Setor Central",
    logradouro: "Av. Goiás, 1530",
    coordenada: "-16.6789, -49.2645",
    cicloId: "CIC-001",
  },
  {
    id: "VST-2026-1283",
    codigoCta: "CTA-GO-5208707-000002",
    especie: "Caesalpinia pluviosa",
    nomePopular: "Sibipiruna",
    projeto: "Implantação do CTA — Goiânia Centro",
    data: "2026-06-18",
    tipo: "Vistoria Técnica",
    tecnico: "Ana Oliveira",
    estadoFitossanitario: "regular",
    classeRisco: "medio",
    recomendacao: "Poda de limpeza",
    fotos: 3,
    status: "Concluída",
    dap: 52,
    altura: 14.0,
    diametroCopa: 10.5,
    parecer: "Galhos secos na porção inferior da copa. Presença de liquens, sem comprometimento estrutural. Copa assimétrica com leve inclinação para oeste. Recomendada poda de limpeza dos galhos secos no próximo ciclo.",
    sintomas: ["Galhos secos", "Liquens"],
    interferencias: ["Rede elétrica a 3m da copa"],
    municipio: "Goiânia",
    uf: "GO",
    bairro: "Setor Marista",
    logradouro: "Rua 1135, 420",
    coordenada: "-16.6890, -49.2720",
    cicloId: "CIC-001",
  },
  {
    id: "VST-2026-1282",
    codigoCta: "CTA-GO-5208707-000003",
    especie: "Licania tomentosa",
    nomePopular: "Oiti",
    projeto: "Inventário de Praça — Praça Universitária",
    data: "2026-06-20",
    tipo: "Vistoria Técnica",
    tecnico: "Rafael Torres",
    estadoFitossanitario: "bom",
    classeRisco: "baixo",
    recomendacao: "Sem intervenção",
    fotos: 5,
    status: "Validada",
    dap: 38,
    altura: 9.8,
    diametroCopa: 7.5,
    parecer: "Indivíduo saudável em praça pública. Boa estrutura de copa. Frutos presentes na época de vistoria. Sem interferências com mobiliário urbano. Recomendada manutenção da área de gola (2m de raio).",
    sintomas: [],
    interferencias: [],
    municipio: "Goiânia",
    uf: "GO",
    bairro: "Setor Universitário",
    logradouro: "Praça Universitária, s/n",
    coordenada: "-16.6812, -49.2567",
    cicloId: "CIC-001",
  },
  {
    id: "VST-2026-1281",
    codigoCta: "CTA-GO-5208707-000004",
    especie: "Mangifera indica",
    nomePopular: "Mangueira",
    projeto: "Inventário de Parque — Parque Municipal Ambiental Norte",
    data: "2026-06-15",
    tipo: "Vistoria Técnica",
    tecnico: "Juliana Costa",
    estadoFitossanitario: "regular",
    classeRisco: "alto",
    recomendacao: "Poda corretiva",
    fotos: 6,
    status: "Em Revisão",
    dap: 78,
    altura: 18.2,
    diametroCopa: 15.0,
    parecer: "Indivíduo centenário de grande porte. Copa assimétrica com 3 galhos pesados sobre área de estar do parque. Raízes expostas em raio de 5m com risco de tropeço. Cavidade de 12cm no tronco a 3m de altura. Recomendada poda corretiva urgente nos galhos sobre área de circulação.",
    sintomas: ["Copa assimétrica", "Cavidade no tronco", "Raízes expostas"],
    interferencias: ["Área de estar do parque", "Caminho de pedestres"],
    municipio: "Goiânia",
    uf: "GO",
    bairro: "Setor Norte",
    logradouro: "Parque Municipal Ambiental Norte, s/n",
    coordenada: "-16.6520, -49.2890",
    cicloId: "CIC-003",
  },
  {
    id: "VST-2026-1280",
    codigoCta: "CTA-GO-5208707-000005",
    especie: "Roystonea oleracea",
    nomePopular: "Palmeira-imperial",
    projeto: "Implantação do CTA — Goiânia Centro",
    data: "2026-06-28",
    tipo: "Monitoramento Semestral",
    tecnico: "Carlos Mendes",
    estadoFitossanitario: "bom",
    classeRisco: "baixo",
    recomendacao: "Monitorar",
    fotos: 3,
    status: "Concluída",
    dap: 35,
    altura: 22.0,
    diametroCopa: 4.5,
    parecer: "Palmeira adulta, estipe reto sem danos estruturais. Folhas basais secas necessitam remoção preventiva para evitar queda sobre pedestres. Monitoramento semestral adequado. Recomendada limpeza de folhas secas.",
    sintomas: ["Folhas basais secas"],
    interferencias: [],
    municipio: "Goiânia",
    uf: "GO",
    bairro: "Setor Central",
    logradouro: "Av. Anhanguera, 3200",
    coordenada: "-16.6750, -49.2610",
    cicloId: "CIC-002",
  },
  {
    id: "VST-2026-1279",
    codigoCta: "CTA-GO-5208707-000006",
    especie: "Delonix regia",
    nomePopular: "Flamboyant",
    projeto: "Loteamento Verde Vale",
    data: "2026-06-10",
    tipo: "Emergencial",
    tecnico: "Ana Oliveira",
    estadoFitossanitario: "ruim",
    classeRisco: "critico",
    recomendacao: "Supressão",
    fotos: 7,
    status: "Em Revisão",
    dap: 28,
    altura: 8.5,
    diametroCopa: 6.8,
    parecer: "ATENÇÃO: Indivíduo inclinado 25° em direção à via pública (Rua das Acácias). Cavidade extensa (40cm) no tronco principal com presença ativa de cupins (Nasutitermes sp.). Raízes com apodrecimento visível no lado da inclinação. Risco iminente de queda. Recomendada supressão imediata com laudo justificativo. Área deve ser isolada até execução.",
    sintomas: ["Inclinação > 20°", "Cavidade extensa", "Cupins ativos", "Podridão radicular"],
    interferencias: ["Via pública - Rua das Acácias", "Rede elétrica a 5m"],
    municipio: "Goiânia",
    uf: "GO",
    bairro: "Jardim Verde",
    logradouro: "Rua das Acácias, 88",
    coordenada: "-16.7010, -49.3120",
    cicloId: "CIC-003",
  },
  {
    id: "VST-2026-1278",
    codigoCta: "CTA-GO-5208707-000009",
    especie: "Ficus benjamina",
    nomePopular: "Ficus",
    projeto: "Monitoramento de Árvores Críticas — Setor Bueno",
    data: "2026-06-12",
    tipo: "Emergencial",
    tecnico: "Pedro Alves",
    estadoFitossanitario: "ruim",
    classeRisco: "critico",
    recomendacao: "Poda emergencial",
    fotos: 9,
    status: "Validada",
    dap: 92,
    altura: 20.5,
    diametroCopa: 18.0,
    parecer: "Sistema radicular agressivo comprometendo calçada (12m lineares com elevação > 5cm) e rede de esgoto (vazamento confirmado pela Saneago). Galhos com rachaduras longitudinais na porção superior. Vistoria trimestral obrigatória. Laudo técnico de risco anexado. Ordem de serviço de poda emergencial emitida (OS-2026-0452).",
    sintomas: ["Rachaduras em galhos", "Raízes comprometendo infraestrutura", "Rede de esgoto danificada"],
    interferencias: ["Calçada danificada (12m)", "Rede de esgoto", "Poste de iluminação a 2m"],
    municipio: "Goiânia",
    uf: "GO",
    bairro: "Setor Bueno",
    logradouro: "Rua T-55, 1200",
    coordenada: "-16.7040, -49.2820",
    cicloId: "CIC-003",
  },
  {
    id: "VST-2026-1277",
    codigoCta: "CTA-GO-5208707-000007",
    especie: "Jacaranda mimosifolia",
    nomePopular: "Jacarandá-mimoso",
    projeto: "Implantação do CTA — Goiânia Centro",
    data: "2026-06-22",
    tipo: "Remedição",
    tecnico: "Pedro Alves",
    estadoFitossanitario: "bom",
    classeRisco: "baixo",
    recomendacao: "Sem intervenção",
    fotos: 4,
    status: "Concluída",
    dap: 34,
    altura: 10.8,
    diametroCopa: 7.2,
    parecer: "Remedição confirma crescimento saudável. DAP aumentou 2cm desde vistoria anterior (jan/2026). Altura aumentou 0,6m. Floração abundante registrada em setembro/2025. Sem alterações significativas. Manter monitoramento anual.",
    sintomas: [],
    interferencias: [],
    municipio: "Goiânia",
    uf: "GO",
    bairro: "Jardim Goiás",
    logradouro: "Alameda das Rosas, 650",
    coordenada: "-16.6950, -49.2780",
    cicloId: "CIC-002",
  },
  {
    id: "VST-2026-1276",
    codigoCta: "CTA-GO-5208707-000011",
    especie: "Hymenaea courbaril",
    nomePopular: "Jatobá",
    projeto: "Projeto Carbono Urbano — Áreas Verdes Municipais",
    data: "2026-06-08",
    tipo: "Monitoramento Anual",
    tecnico: "Gabriel Santos",
    estadoFitossanitario: "regular",
    classeRisco: "medio",
    recomendacao: "Tratamento fitossanitário",
    fotos: 5,
    status: "Em Revisão",
    dap: 65,
    altura: 16.8,
    diametroCopa: 12.5,
    parecer: "Indivíduo adulto de alto valor ecológico e de carbono. Presença de cochonilhas (Coccidae) na casca do tronco principal e galhos secundários. Infestação moderada, sem comprometimento estrutural aparente. Recomendado tratamento fitossanitário com óleo mineral + controle biológico (joaninhas). Manter em monitoramento trimestral até controle da praga.",
    sintomas: ["Cochonilhas", "Fumagina leve"],
    interferencias: [],
    municipio: "Goiânia",
    uf: "GO",
    bairro: "Setor Sul",
    logradouro: "Praça Cívica, s/n",
    coordenada: "-16.6795, -49.2578",
    cicloId: "CIC-002",
  },
  {
    id: "VST-2026-1275",
    codigoCta: "CTA-GO-5208707-000008",
    especie: "Paubrasilia echinata",
    nomePopular: "Pau-brasil",
    projeto: "Inventário de UC — Parque Natural Municipal",
    data: "2026-06-15",
    tipo: "UC / Área Verde",
    tecnico: "Fernanda Lima",
    estadoFitossanitario: "bom",
    classeRisco: "baixo",
    recomendacao: "Monitorar",
    fotos: 5,
    status: "Validada",
    dap: 23,
    altura: 7.2,
    diametroCopa: 4.5,
    parecer: "Indivíduo jovem plantado em programa de reintrodução de espécies ameaçadas (2023). Excelente desenvolvimento. DAP aumentou 3cm desde plantio. Sem sinais de pragas ou doenças. Acompanhamento trimestral mantido conforme protocolo da UC.",
    sintomas: [],
    interferencias: [],
    municipio: "Goiânia",
    uf: "GO",
    bairro: "Setor Leste",
    logradouro: "Parque Natural Municipal, s/n",
    coordenada: "-16.6880, -49.2450",
    cicloId: "CIC-004",
  },
  {
    id: "VST-2026-1274",
    codigoCta: "CTA-GO-5208707-000010",
    especie: "Tibouchina granulosa",
    nomePopular: "Quaresmeira",
    projeto: "Compensação Ambiental — Corredor Verde Sul",
    data: "2026-06-05",
    tipo: "Pós-replantio",
    tecnico: "Marcos Vinicius",
    estadoFitossanitario: "bom",
    classeRisco: "baixo",
    recomendacao: "Sem intervenção",
    fotos: 3,
    status: "Concluída",
    dap: 18,
    altura: 5.5,
    diametroCopa: 3.8,
    parecer: "Pós-replantio de 2 anos. Indivíduo com excelente adaptação ao local. Floração roxa intensa registrada em março/2026. Tutor em bom estado, sem necessidade de substituição. Rega suplementar mantida no período seco. Área de gola com cobertura vegetal adequada.",
    sintomas: [],
    interferencias: [],
    municipio: "Goiânia",
    uf: "GO",
    bairro: "Corredor Verde Sul",
    logradouro: "Av. Corredor Verde, s/n",
    coordenada: "-16.7200, -49.2950",
    cicloId: "CIC-005",
  },
  {
    id: "VST-2026-1273",
    codigoCta: "CTA-GO-5208707-000012",
    especie: "Myracrodruon urundeuva",
    nomePopular: "Aroeira",
    projeto: "Compensação Ambiental — Corredor Verde Sul",
    data: "2026-05-20",
    tipo: "Vistoria Técnica",
    tecnico: "Marcos Vinicius",
    estadoFitossanitario: "bom",
    classeRisco: "baixo",
    recomendacao: "Replantio",
    fotos: 2,
    status: "Vencida",
    dap: 6,
    altura: 1.8,
    diametroCopa: 0.8,
    parecer: "Muda de 6 meses com tutor quebrado próximo à base. Planta inclinada 15° (corrigível). Recomendado replantio para correção de alinhamento e substituição de tutor. Ação pendente há 41 dias.",
    sintomas: ["Tutor danificado", "Inclinação leve"],
    interferencias: [],
    municipio: "Goiânia",
    uf: "GO",
    bairro: "Corredor Verde Sul",
    logradouro: "Av. Corredor Verde, s/n",
    coordenada: "-16.7210, -49.2960",
    cicloId: "CIC-005",
  },
  {
    id: "VST-2026-1272",
    codigoCta: "CTA-GO-5208707-000013",
    especie: "Tabebuia roseoalba",
    nomePopular: "Ipê-branco",
    projeto: "Implantação do CTA — Goiânia Centro",
    data: "2026-05-15",
    tipo: "Vistoria Técnica",
    tecnico: "Ana Oliveira",
    estadoFitossanitario: "bom",
    classeRisco: "baixo",
    recomendacao: "Monitorar",
    fotos: 3,
    status: "Concluída",
    dap: 28,
    altura: 9.2,
    diametroCopa: 6.5,
    parecer: "Ipê-branco em excelente estado vegetativo. Copa equilibrada e simétrica. Floração intensa registrada em agosto/2025. Sem interferências com estruturas urbanas. Recomendada manutenção da gola de 1,5m.",
    sintomas: [],
    interferencias: [],
    municipio: "Goiânia",
    uf: "GO",
    bairro: "Setor Central",
    logradouro: "Av. Tocantins, 780",
    coordenada: "-16.6820, -49.2580",
    cicloId: "CIC-001",
  },
  {
    id: "VST-2026-1271",
    codigoCta: "CTA-GO-5208707-000014",
    especie: "Schinus molle",
    nomePopular: "Aroeira-salsa",
    projeto: "Loteamento Verde Vale",
    data: "2026-05-08",
    tipo: "Vistoria Técnica",
    tecnico: "Fernanda Lima",
    estadoFitossanitario: "regular",
    classeRisco: "medio",
    recomendacao: "Poda de limpeza",
    fotos: 2,
    status: "Rascunho",
    dap: 22,
    altura: 7.8,
    diametroCopa: 5.2,
    parecer: "",
    sintomas: [],
    interferencias: ["Muro do lote 14 a 1,5m"],
    municipio: "Goiânia",
    uf: "GO",
    bairro: "Jardim Verde",
    logradouro: "Rua das Orquídeas, 215",
    coordenada: "-16.7030, -49.3140",
    cicloId: null,
  },
  {
    id: "VST-2026-1270",
    codigoCta: "CTA-GO-5208707-000015",
    especie: "Caryocar brasiliense",
    nomePopular: "Pequi",
    projeto: "Projeto Carbono Urbano — Áreas Verdes Municipais",
    data: "2026-04-22",
    tipo: "Monitoramento Anual",
    tecnico: "Gabriel Santos",
    estadoFitossanitario: "bom",
    classeRisco: "baixo",
    recomendacao: "Sem intervenção",
    fotos: 4,
    status: "Validada",
    dap: 42,
    altura: 11.0,
    diametroCopa: 9.0,
    parecer: "Indivíduo nativo do Cerrado, adulto, em excelente estado. Importante para biodiversidade local e sequestro de carbono. Frutificação abundante (dez/2025 a fev/2026). Sem interferências. Área de gola preservada com 2,5m.",
    sintomas: [],
    interferencias: [],
    municipio: "Goiânia",
    uf: "GO",
    bairro: "Setor Sul",
    logradouro: "Praça Cívica, s/n",
    coordenada: "-16.6800, -49.2565",
    cicloId: "CIC-002",
  },
  {
    id: "VST-2026-1269",
    codigoCta: "CTA-GO-5208707-000009",
    especie: "Ficus benjamina",
    nomePopular: "Ficus",
    projeto: "Monitoramento de Árvores Críticas — Setor Bueno",
    data: "2026-03-15",
    tipo: "Pós-poda",
    tecnico: "Pedro Alves",
    estadoFitossanitario: "regular",
    classeRisco: "alto",
    recomendacao: "Monitorar",
    fotos: 6,
    status: "Concluída",
    dap: 91,
    altura: 19.8,
    diametroCopa: 17.5,
    parecer: "Vistoria pós-poda emergencial (OS-2026-0098). Galhos com rachaduras removidos. Cicatrização em andamento, sem sinais de infecção. Calçada ainda necessita reparos. Rede de esgoto reparada pela Saneago. Risco reduzido de crítico para alto. Próxima vistoria em jun/2026.",
    sintomas: ["Cicatrização em andamento", "Calçada danificada"],
    interferencias: ["Calçada danificada", "Poste de iluminação"],
    municipio: "Goiânia",
    uf: "GO",
    bairro: "Setor Bueno",
    logradouro: "Rua T-55, 1200",
    coordenada: "-16.7040, -49.2820",
    cicloId: "CIC-003",
  },
  {
    id: "VST-2026-1268",
    codigoCta: "CTA-GO-5208707-000004",
    especie: "Mangifera indica",
    nomePopular: "Mangueira",
    projeto: "Inventário de Parque — Parque Municipal Ambiental Norte",
    data: "2026-03-10",
    tipo: "Vistoria Técnica",
    tecnico: "Juliana Costa",
    estadoFitossanitario: "bom",
    classeRisco: "medio",
    recomendacao: "Monitorar",
    fotos: 4,
    status: "Concluída",
    dap: 76,
    altura: 17.8,
    diametroCopa: 14.5,
    parecer: "Vistoria de rotina. Mangueira centenária em bom estado geral. Copa com leve assimetria, sem galhos comprometidos. Raízes expostas monitoradas — sem alteração desde última vistoria.",
    sintomas: ["Copa assimétrica leve"],
    interferencias: ["Área de estar"],
    municipio: "Goiânia",
    uf: "GO",
    bairro: "Setor Norte",
    logradouro: "Parque Municipal Ambiental Norte, s/n",
    coordenada: "-16.6520, -49.2890",
    cicloId: "CIC-003",
  },
  {
    id: "VST-2026-1267",
    codigoCta: "CTA-GO-5208707-000016",
    especie: "Erythrina speciosa",
    nomePopular: "Mulungu",
    projeto: "Implantação do CTA — Goiânia Centro",
    data: "2026-02-28",
    tipo: "Vistoria Técnica",
    tecnico: "Carlos Mendes",
    estadoFitossanitario: "regular",
    classeRisco: "medio",
    recomendacao: "Poda corretiva",
    fotos: 3,
    status: "Reprovada",
    dap: 35,
    altura: 9.0,
    diametroCopa: 7.0,
    parecer: "Parecer anterior reprovado pelo responsável técnico. Necessário complementar avaliação de risco com tomografia de tronco. Cavidade suspeita detectada no exame visual, mas sem confirmação instrumental.",
    sintomas: ["Cavidade suspeita no tronco"],
    interferencias: [],
    municipio: "Goiânia",
    uf: "GO",
    bairro: "Setor Marista",
    logradouro: "Rua 1136, 520",
    coordenada: "-16.6910, -49.2750",
    cicloId: null,
  },
];

export const ciclosMonitoramento: MonitoringCycle[] = [
  {
    id: "CIC-001",
    nome: "Inventário Inicial 2026",
    tipo: "Inventário Inicial",
    projeto: "Implantação do CTA — Goiânia Centro",
    dataInicio: "2026-04-10",
    dataFim: "2026-12-15",
    status: "Em andamento",
    arvoresPrevistas: 3850,
    vistoriasRealizadas: 1723,
    progresso: 45,
    pendencias: 89,
  },
  {
    id: "CIC-002",
    nome: "Monitoramento Anual 2027",
    tipo: "Monitoramento Anual",
    projeto: "Projeto Carbono Urbano — Áreas Verdes Municipais",
    dataInicio: "2027-01-05",
    dataFim: "2027-03-30",
    status: "Planejado",
    arvoresPrevistas: 8920,
    vistoriasRealizadas: 0,
    progresso: 0,
    pendencias: 0,
  },
  {
    id: "CIC-003",
    nome: "Revisão de Árvores Críticas",
    tipo: "Monitoramento Emergencial",
    projeto: "Monitoramento de Árvores Críticas — Setor Bueno",
    dataInicio: "2026-03-10",
    dataFim: "2026-09-10",
    status: "Em andamento",
    arvoresPrevistas: 89,
    vistoriasRealizadas: 48,
    progresso: 55,
    pendencias: 15,
  },
  {
    id: "CIC-004",
    nome: "Inventário de UC — Parque Natural Municipal",
    tipo: "Inventário de UC",
    projeto: "Inventário de UC — Parque Natural Municipal",
    dataInicio: "2026-07-15",
    dataFim: "2026-12-20",
    status: "Planejado",
    arvoresPrevistas: 0,
    vistoriasRealizadas: 0,
    progresso: 5,
    pendencias: 0,
  },
  {
    id: "CIC-005",
    nome: "Replantio Compensatório — Corredor Verde Sul",
    tipo: "Pós-replantio",
    projeto: "Compensação Ambiental — Corredor Verde Sul",
    dataInicio: "2025-09-01",
    dataFim: "2028-09-01",
    status: "Em andamento",
    arvoresPrevistas: 2150,
    vistoriasRealizadas: 2150,
    progresso: 100,
    pendencias: 0,
  },
];

export const comparacaoTemporal: TemporalComparison[] = [
  {
    codigoCta: "CTA-GO-5208707-000007",
    especie: "Jacaranda mimosifolia",
    nomePopular: "Jacarandá-mimoso",
    vistoriaAnterior: {
      id: "VST-2026-1150",
      data: "2026-01-18",
      dap: 32,
      altura: 10.2,
      estado: "bom",
      risco: "baixo",
      recomendacao: "Sem intervenção",
    },
    vistoriaAtual: {
      id: "VST-2026-1277",
      data: "2026-06-22",
      dap: 34,
      altura: 10.8,
      estado: "bom",
      risco: "baixo",
      recomendacao: "Sem intervenção",
    },
    tendencia: "estavel",
  },
  {
    codigoCta: "CTA-GO-5208707-000009",
    especie: "Ficus benjamina",
    nomePopular: "Ficus",
    vistoriaAnterior: {
      id: "VST-2026-1269",
      data: "2026-03-15",
      dap: 91,
      altura: 19.8,
      estado: "regular",
      risco: "alto",
      recomendacao: "Monitorar",
    },
    vistoriaAtual: {
      id: "VST-2026-1278",
      data: "2026-06-12",
      dap: 92,
      altura: 20.5,
      estado: "ruim",
      risco: "critico",
      recomendacao: "Poda emergencial",
    },
    tendencia: "agravado",
  },
  {
    codigoCta: "CTA-GO-5208707-000004",
    especie: "Mangifera indica",
    nomePopular: "Mangueira",
    vistoriaAnterior: {
      id: "VST-2026-1268",
      data: "2026-03-10",
      dap: 76,
      altura: 17.8,
      estado: "bom",
      risco: "medio",
      recomendacao: "Monitorar",
    },
    vistoriaAtual: {
      id: "VST-2026-1281",
      data: "2026-06-15",
      dap: 78,
      altura: 18.2,
      estado: "regular",
      risco: "alto",
      recomendacao: "Poda corretiva",
    },
    tendencia: "agravado",
  },
];

export const alertasPrioritariosVistoria = [
  {
    id: "APV-001",
    titulo: "Vistorias vencidas há mais de 30 dias",
    descricao: "12 vistorias com prazo de execução excedido em mais de 30 dias. Inclui árvores com recomendação de supressão e poda emergencial.",
    nivel: "critico",
    quantidade: 12,
    icone: "ri-timer-line",
  },
  {
    id: "APV-002",
    titulo: "Árvores críticas sem remedição",
    descricao: "8 árvores classificadas como risco crítico não receberam nova vistoria nos últimos 90 dias.",
    nivel: "critico",
    quantidade: 8,
    icone: "ri-alert-line",
  },
  {
    id: "APV-003",
    titulo: "Registros em revisão",
    descricao: "326 vistorias aguardando revisão técnica. Tempo médio de espera: 18 dias.",
    nivel: "alto",
    quantidade: 326,
    icone: "ri-file-search-line",
  },
  {
    id: "APV-004",
    titulo: "Vistorias sem foto",
    descricao: "47 registros de vistoria sem documentação fotográfica anexada.",
    nivel: "medio",
    quantidade: 47,
    icone: "ri-camera-off-line",
  },
  {
    id: "APV-005",
    titulo: "Vistorias sem parecer técnico",
    descricao: "112 vistorias concluídas sem parecer técnico preenchido. Obrigatório para validação.",
    nivel: "alto",
    quantidade: 112,
    icone: "ri-file-text-line",
  },
  {
    id: "APV-006",
    titulo: "Árvores com risco agravado",
    descricao: "15 árvores apresentaram agravamento do risco entre a vistoria anterior e a atual. Revisão urgente necessária.",
    nivel: "alto",
    quantidade: 15,
    icone: "ri-arrow-up-circle-line",
  },
];